import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../services/api';
import { EnvelopeIcon, CheckCircleIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

const VerifyEmail = () => {
  const query = useQuery();
  const email = query.get('email') || '';
  const isRegistration = query.get('registration') === 'true'; // Yeni eklenen
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [registerUserData, setRegisterUserData] = useState(null);
  const navigate = useNavigate();

  // Cooldown timer
  useEffect(() => {
    let timer;
    if (resendCooldown > 0) {
      timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  // Register sayfasından gelen user verisini kontrol et
  useEffect(() => {
    // Eğer register sırasında yönlendirme olduysa, user verisi sessionStorage'da tutulmuş olabilir
    const userDataStr = sessionStorage.getItem('pendingUser');
    if (userDataStr) {
      try {
        setRegisterUserData(JSON.parse(userDataStr));
      } catch {
        setRegisterUserData(null);
      }
    }
  }, []);

  const handleVerify = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      const params = new URLSearchParams({ email, code });
      const response = await api.post(`/v1/verification/verify-email-code?${params.toString()}`);
      
      if (response.data.success) {
        setSuccess(true);
        // Doğrulama başarılıysa, registerUserData varsa localStorage'a kaydet
        if (registerUserData) {
          localStorage.setItem('user', JSON.stringify(registerUserData));
          // Navbar güncellemesi için event tetikle
          window.dispatchEvent(new Event('user-updated'));
          // Temizle
          sessionStorage.removeItem('pendingUser');
        }
        // Kaydolma sırasındaki doğrulama ise profil fotoğrafı sayfasına, değilse anasayfaya yönlendir
        setTimeout(() => {
          if (isRegistration) {
            navigate(`/upload-profile-image`);
          } else {
            navigate('/');
          }
        }, 1500);
      } else {
        setError(response.data.message || 'Doğrulama başarısız. Lütfen kodu kontrol edin.');
      }
    } catch (err) {
      setError(
        err?.response?.data?.message ||
        'Doğrulama başarısız. Lütfen kodu kontrol edin.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleResend = async () => {
    setError('');
    setResendCooldown(300); // 5 dakika = 300 saniye
    try {
      await api.post(`/v1/verification/send-email-code?email=${encodeURIComponent(email)}`);
    } catch (err) {
      setError('Kod tekrar gönderilemedi. Lütfen daha sonra tekrar deneyin.');
      setResendCooldown(0);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-blue-50 to-white dark:from-gray-900 dark:to-gray-800 px-4">
      <div className="max-w-md w-full bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6 sm:p-8">
        <div className="flex flex-col items-center mb-6">
          <EnvelopeIcon className="h-12 w-12 text-blue-600 mb-2" />
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-1">E-posta Doğrulama</h2>
          <p className="text-sm text-gray-600 dark:text-gray-300 text-center">
            <span className="font-medium">{email}</span> adresine bir doğrulama kodu gönderdik.<br />
            Lütfen e-posta kutunuzu kontrol edin ve aşağıya kodu girin.
          </p>
        </div>
        <form onSubmit={handleVerify} className="space-y-4">
          <div>
            <label htmlFor="code" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Doğrulama Kodu
            </label>
            <input
              id="code"
              name="code"
              type="text"
              value={code}
              onChange={e => setCode(e.target.value)}
              required
              maxLength={8}
              className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-800 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-base"
              placeholder="Kodunuzu girin"
              autoFocus
              disabled={submitting || success}
            />
          </div>
          {error && (
            <div className="flex items-center text-red-500 text-sm mt-1">
              <ExclamationCircleIcon className="h-5 w-5 mr-1" />
              {error}
            </div>
          )}
          {success && (
            <div className="flex items-center text-green-600 text-sm mt-1">
              <CheckCircleIcon className="h-5 w-5 mr-1" />
              Doğrulama başarılı! Yönlendiriliyorsunuz...
            </div>
          )}
          <button
            type="submit"
            disabled={submitting || success || !code}
            className={`w-full py-3 rounded-xl font-medium text-white bg-blue-600 hover:bg-blue-700 transition-colors shadow ${
              (submitting || success || !code) ? 'opacity-70 cursor-not-allowed' : ''
            }`}
          >
            {submitting ? 'Doğrulanıyor...' : 'Doğrula'}
          </button>
        </form>
        <div className="mt-4 text-center">
          <button
            type="button"
            onClick={handleResend}
            disabled={submitting || resendCooldown > 0}
            className="text-blue-600 dark:text-blue-400 hover:underline text-sm"
          >
            {resendCooldown > 0
              ? `Tekrar kod göndermek için ${Math.floor(resendCooldown / 60)}:${(resendCooldown % 60).toString().padStart(2, '0')} dk bekleyin`
              : 'Kodu tekrar gönder'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default VerifyEmail;
